import { px } from '@adsk/alloy-react-helpers';

import { AlloyLegacyThemeDesignTokens, TextareaDesignTokens } from '../types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

export const getTextareaTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): TextareaDesignTokens => {
  const { tokens, typography, borderRadius, spacing, halos, colors } = theme;
  const { colors: tokenColors } = tokens;

  return {
    container: {
      _: {
        'font-family': typography.labelMedium.fontFamily,
        'font-weight': typography.labelMedium.fontWeight,
        'font-size': typography.labelMedium.fontSize,
        'line-height': typography.labelMedium.lineHeight,
        color: tokenColors.text.primary.value,
        'border-width': '1px',
        'border-style': 'solid',
        'border-color': tokenColors.border.value,
        'border-top-left-radius': px(borderRadius.small),
        'border-top-right-radius': px(borderRadius.small),
        'border-bottom-right-radius': px(borderRadius.small),
        'border-bottom-left-radius': px(borderRadius.small),
        'box-sizing': 'border-box',
        position: 'relative',
        margin: 0,
        'padding-top': 0,
        'padding-right': 0,
        'padding-left': 0,
      },
      disabled: {
        true: {
          background: tokenColors.background.neutral.inactive.value,
          'border-color': tokenColors.border.inactive.value,
          'box-shadow': 'none',
          color: tokenColors.text.inactive.value,
          cursor: 'not-allowed',
        },
        false: {
          transition: theme.transitions.create(['box-shadow', 'border-color']),
          '&:hover': {
            'box-shadow': halos.hover,
          },
          '&:focus-within': {
            'box-shadow': halos.focus,
          },
          '&:active': {
            'box-shadow': halos.active,
            'border-color': colors.charcoal500,
          },
        },
      },
      error: {
        true: {
          'border-color': tokenColors.border.error.value,
        },
        false: {
          '&:focus:not(:active)': {
            'border-color': tokenColors.border.focused.value,
          },
        },
      },
      counter: {
        true: { 'padding-bottom': px(28) },
        false: { 'padding-bottom': 0 },
      },
    },
    input: {
      _: {
        outline: 'none',
        resize: 'none',
        border: 'none',
        overflow: 'auto',
        width: '100%',
        'padding-top': px(spacing.XS),
        'padding-bottom': px(spacing.XS),
        'padding-left': px(spacing.S),
        'padding-right': px(spacing.S),
        '&::placeholder': {
          color: tokenColors.text.tertiary.value,
        },
        '&:disabled': {
          background: tokenColors.background.neutral.inactive.value,
          color: tokenColors.text.inactive.value,
          cursor: 'not-allowed',
          'box-shadow': 'none',
        },
      },
    },
    count: {
      _: {
        'pointer-events': 'none',
        position: 'absolute',
        right: px(spacing.XXS),
        bottom: px(spacing.XXS),
      },
      textColor: {
        idle: { color: tokenColors.text.secondary.value },
        disabled: { color: tokenColors.text.inactive.value },
        error: { color: tokenColors.text.error.value },
      },
    },
  };
};

export const getTextareaTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): TextareaDesignTokens => {
  const input = tokens?.component?.input;
  const generic = tokens?.generic;

  const disabledOpacity = input?.opacity?.disabled;

  return {
    container: {
      _: {
        'font-family': fontFamily,
        'font-weight': input?.['font-weight'],
        'font-size': input?.['font-size'],
        'line-height': input?.['line-height'],
        color: input?.['text-color'],
        'border-width': input?.box?.['border-width'],
        'border-style': input?.box?.['border-style'],
        'border-color': input?.box?.['border-color'],
        'border-top-left-radius': input?.['border-top-left-radius'],
        'border-top-right-radius': input?.['border-top-right-radius'],
        'border-bottom-right-radius': input?.['border-bottom-right-radius'],
        'border-bottom-left-radius': input?.['border-bottom-left-radius'],
        'box-sizing': 'border-box',
        position: 'relative',
        margin: '0',
        'padding-top': 0,
        'padding-right': 0,
        'padding-left': 0,
      },
      disabled: {
        true: {
          'box-shadow': 'none',
          cursor: 'not-allowed',
        },
        false: {
          transition: `box-shadow ${input?.['transition-duration'] || '200ms'} ${input?.['transition-timing-function'] || 'ease'}, border-color ${input?.['transition-duration'] || '200ms'} ${input?.['transition-timing-function'] || 'ease'}`,
          '&:hover': {
            'box-shadow': input?.['box-shadow']?.hover,
          },
          '&:focus-within': {
            'box-shadow': input?.['box-shadow']?.focus,
          },
          '&:active': {
            'box-shadow': input?.['box-shadow']?.hover,
            'border-color': input?.['border-bottom-color']?.hover,
          },
        },
      },
      error: {
        true: {
          'border-color': input?.error?.['border-bottom-color'],
        },
        false: {
          '&:focus:not(:active)': {
            'border-color': input?.['border-bottom-color']?.focus,
          },
        },
      },
      counter: {
        true: { 'padding-bottom': '28px' },
        false: { 'padding-bottom': 0 },
      },
    },
    input: {
      _: {
        outline: 'none',
        resize: 'none',
        border: 'none',
        overflow: 'auto',
        width: '100%',
        background: 'transparent',
        color: 'inherit',
        'padding-top': input?.box?.['padding-top'],
        'padding-bottom': input?.box?.['padding-bottom'],
        'padding-left': input?.box?.['padding-left'],
        'padding-right': input?.box?.['padding-right'],
        '&::placeholder': {
          color: input?.placeholder?.['text-color'],
        },
        '&:disabled': {
          opacity: disabledOpacity,
          cursor: 'not-allowed',
          'box-shadow': 'none',
        },
      },
    },
    count: {
      _: {
        'pointer-events': 'none',
        position: 'absolute',
        right: generic?.spacing?.[4],
        bottom: generic?.spacing?.[4],
      },
      textColor: {
        idle: { color: generic?.color?.charcoal?.[600]?.[100] },
        disabled: { opacity: disabledOpacity },
        error: { color: input?.error?.['border-bottom-color'] },
      },
    },
  };
};
